import { useEffect, useRef, useState } from 'react';
import type { ListItem } from '../LiliAutoComplete';
import type { AutoCompleteCompleteEvent } from 'primereact/autocomplete';

export const useAutoCompleteLogic = (
  autoCompleteItems: ListItem[],
  selectedItem?: ListItem | null
) => {
  const [filterInputValue, setFilterInputValue] = useState<string>(selectedItem?.title || '');
  const [suggestedItems, setSuggestedItems] = useState<ListItem[]>(autoCompleteItems);
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [wasKeyboardNavigation, setWasKeyboardNavigation] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const highlightedItemRef = useRef<HTMLButtonElement>(null);

  const resetHighlightState = () => {
    setHighlightedIndex(-1);
    setWasKeyboardNavigation(false);
  };

  const closePanelAndResetHighlight = () => {
    setIsPanelVisible(false);
    resetHighlightState();
  };

  const setSuggestionsOnChange = (e: AutoCompleteCompleteEvent) => {
    const searchKey = e.query.trim();
    setSuggestedItems(
      autoCompleteItems.filter(
        (item) => item?.persist || item.title.toLowerCase().includes(searchKey.toLowerCase())
      )
    );
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent, onItemSelection: (item: ListItem) => void) => {
    if (!isPanelVisible || suggestedItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setWasKeyboardNavigation(true);
        setHighlightedIndex((prevIndex) =>
          prevIndex < suggestedItems.length - 1 ? prevIndex + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setWasKeyboardNavigation(true);
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestedItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestedItems.length) {
          onItemSelection(suggestedItems[highlightedIndex]);
          closePanelAndResetHighlight();
        }
        break;
      case 'Escape':
        e.preventDefault();
        closePanelAndResetHighlight();
        break;
    }
  };

  useEffect(() => {
    if (!selectedItem) {
      setFilterInputValue('');
    } else {
      setFilterInputValue(selectedItem?.title?.trimStart() || filterInputValue || '');
    }
    setSuggestedItems(autoCompleteItems);
    setHighlightedIndex(-1); // Reset highlighted index when items change
  }, [selectedItem, autoCompleteItems]);

  // Reset highlighted index when panel visibility changes
  useEffect(() => {
    if (!isPanelVisible) {
      resetHighlightState();
      setSuggestedItems(autoCompleteItems);
    }
  }, [isPanelVisible, autoCompleteItems]);

  // Scroll highlighted item into view when navigating with keyboard
  useEffect(() => {
    if (wasKeyboardNavigation && highlightedIndex >= 0 && highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
      setWasKeyboardNavigation(false);
    }
  }, [highlightedIndex, wasKeyboardNavigation]);

  return {
    componentRef,
    filterInputValue,
    suggestedItems,
    isPanelVisible,
    highlightedIndex,
    highlightedItemRef,
    setSuggestionsOnChange,
    setFilterInputValue,
    setIsPanelVisible,
    setHighlightedIndex,
    handleKeyDown,
    closePanelAndResetHighlight,
  };
};
